import { useId } from "react";
import PropTypes from "prop-types";
import Box from "@mui/material/Box";
import Dialog from "@mui/material/Dialog";
import GrIconButton from "./GrIconButton";
import GrIcon from "./GrIcon";
import icons from "../icons";
import { gr } from "../tokens";

// Dialog: radius 20, title 18/600, body, actions right-aligned (quiet Cancel + one primary).
// On phones it uses the full width minus 16px margins.
// `align="top"` keeps it near the top so it grows downwards when its content changes (a form that
// adds fields) instead of jumping around the centre. `titleIcon` sits before the title; `showClose`
// adds a ghost × in the top-right corner (for popups without a Cancel button: likes, coins, create post).
// Esc and the backdrop close it either way.
// `variant="media"` (a post with its photos, opened large): no padding, the children fill the dialog
// and scroll inside it (so the × stays put), `fillHeight` makes it as tall as the window allows, and on
// phones it becomes a full-screen sheet with the × on a white chip (it can sit over a photo).
// `fullScreen`: the whole window, no margins or rounding (a post with photos / video, as photo viewers do);
// its × moves to the top-left over the media.
const PHONE = "@media (max-width: 599.95px)";
export default function GrDialog({
  open,
  onClose,
  title,
  children,
  actions,
  maxWidth = 480,
  titleIcon,
  showClose = false,
  align = "center",
  variant = "default",
  fillHeight = false,
  fullScreen = false,
  ...rest
}) {
  const titleId = `gr-dialog-${useId()}`;
  const media = variant === "media";
  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullScreen={fullScreen}
      aria-labelledby={title ? titleId : undefined}
      BackdropProps={{ sx: { background: gr.color.overlay } }}
      sx={
        align === "top"
          ? { "& .MuiDialog-container": { alignItems: "flex-start" } }
          : undefined
      }
      PaperProps={{
        sx: {
          width: "100%",
          maxWidth,
          m: gr.space[4],
          ...(align === "top" ? { mt: gr.space[12] } : {}),
          borderRadius: gr.radius.card,
          boxShadow: gr.shadow.raised,
          padding: gr.space[6],
          fontFamily: gr.font.family,
          color: gr.color.ink,
          backgroundImage: "none",
          position: "relative",
          ...(media
            ? {
                padding: 0,
                overflow: "hidden",
                maxHeight: `calc(100% - ${gr.space[8]} * 2)`,
                ...(fillHeight
                  ? { height: `calc(100% - ${gr.space[8]} * 2)` }
                  : {}),
                [PHONE]: {
                  m: 0,
                  maxWidth: "100%",
                  height: "100%",
                  maxHeight: "100%",
                  borderRadius: 0,
                },
              }
            : {}),
          ...(fullScreen
            ? {
                m: 0,
                width: "100%",
                maxWidth: "100%",
                height: "100%",
                maxHeight: "100%",
                borderRadius: 0,
              }
            : {}),
        },
      }}
      {...rest}
    >
      {showClose ? (
        <GrIconButton
          variant="ghost"
          aria-label="Close"
          onClick={onClose}
          sx={{
            position: "absolute",
            top: gr.space[4],
            right: gr.space[4],
            zIndex: 1,
            // Full window (a photo / video viewer): the × sits top-left on the dark media, as photo
            // viewers place it, in a dark round chip so it reads over any photo.
            ...(fullScreen
              ? {
                  right: "auto",
                  left: gr.space[4],
                  color: gr.color.onBrand,
                  background: gr.color.overlay,
                  borderRadius: gr.radius.pill,
                  "&:hover": { background: gr.color.ink },
                }
              : media
              ? {
                  [PHONE]: {
                    top: gr.space[3],
                    right: gr.space[3],
                    background: gr.color.surface,
                    borderRadius: gr.radius.pill,
                    boxShadow: gr.shadow.raised,
                  },
                }
              : {}),
          }}
        >
          <GrIcon icon={icons.close} />
        </GrIconButton>
      ) : null}
      {title ? (
        <Box
          component="h2"
          id={titleId}
          sx={{
            m: 0,
            mb: gr.space[2],
            display: "flex",
            alignItems: "center",
            gap: gr.space[2],
            overflowWrap: "anywhere",
            // Long titles wrap before the × instead of running under it.
            pr: showClose ? gr.space[10] : 0,
            fontSize: gr.font.size.xl,
            fontWeight: gr.font.weight.medium,
            color: gr.color.ink,
          }}
        >
          {titleIcon}
          {title}
        </Box>
      ) : null}
      <Box
        sx={{
          fontSize: gr.font.size.md,
          color: gr.color.ink2,
          lineHeight: gr.font.lineHeight.normal,
          ...(media
            ? {
                display: "flex",
                flexDirection: "column",
                flex: "1 1 auto",
                minHeight: 0,
              }
            : {}),
        }}
      >
        {children}
      </Box>
      {actions ? (
        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            flexWrap: "wrap",
            gap: gr.space[3],
            mt: gr.space[6],
          }}
        >
          {actions}
        </Box>
      ) : null}
    </Dialog>
  );
}

GrDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func,
  title: PropTypes.node,
  children: PropTypes.node,
  actions: PropTypes.node,
  maxWidth: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  titleIcon: PropTypes.node,
  showClose: PropTypes.bool,
  align: PropTypes.oneOf(["center", "top"]),
  variant: PropTypes.oneOf(["default", "media"]),
  fillHeight: PropTypes.bool,
  fullScreen: PropTypes.bool,
};
